import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { IEditorProps } from 'components/Editors/Editor';
import moment from 'moment';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { getMomentForEditor } from 'components/Editors/utils/EditorDateTimeUtils';
import {
  submitDateTimeValue,
  submitDateTimeValueAsString,
} from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { getMoment, getISODateStringWithUtcOffset, getFormattedDateStringForServer } from 'utils/DateTimeUtils';
import { DateTimePicker } from 'components/inputs/date-time/DateTimePicker';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { EditorLabel } from 'components/Editors/EditorLabel';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { getChildValue } from 'components/property-editors/utils/ProviderNodeUtils';
import { getPortalSetup } from 'utils/PortalSetup';
// import './DatePickerEditor.less'

interface IDatePickerEditorProps extends IEditorProps {
  isDateTimePicker?: boolean;
}

// const getClassName = () => classNames('dpc-date-picker-editor');

/**
 * Basic Date Picker Component
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function DatePickerEditor({ child, path, hideInputLabel, isDateTimePicker }: IDatePickerEditorProps) {
  const [currentDate, setCurrentDate] = useState<moment.Moment>();
  const { editingContextId } = useContext(PropertyEditorContext);
  const validation = getEditorValidationSummary(child);
  const isDate: boolean = getEditorConfigValue(child, 'IsDate'); // wat?
  const isNullable: boolean = getEditorConfigValue(child, 'IsNullable');
  const portalSetup = useMemo(() => getPortalSetup(), []);

  const childValue = getChildValue(child);

  useEffect(() => {
    const date = getMomentForEditor(childValue, isDate);

    if (date) {
      setCurrentDate(date);
    } else if (!isNullable && !currentDate) {
      // if it's not a nullable editor, it should set a default value:
      const date = getMoment()();
      setCurrentDate(date);
      submitDateTimeToServer(date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDate, childValue]);

  const submitDateTimeToServer = useCallback(
    (date: moment.Moment) => {
      if (portalSetup.UseTimeZoneTranslation) {
        const dateTime = getISODateStringWithUtcOffset(date.toDate()) as any;
        submitDateTimeValue({ editingContextId, path, dateTime });
      } else {
        //does not matter what user culture has been specified for the portal while sending the datetime to the server always send in the default culture.
        //server do not care about user culture instead it only care about actual date value
        const dateTime = getFormattedDateStringForServer(date, !isDateTimePicker);
        submitDateTimeValueAsString({ editingContextId, path, dateTime });
      }
    },
    [editingContextId, isDateTimePicker, path, portalSetup.UseTimeZoneTranslation]
  );

  const onChange = useCallback(
    (date?: moment.Moment) => {
      if (!date && isNullable) {
        submitDateTimeValueAsString({ editingContextId, path, dateTime: '' });
        return;
      } else if (!date) {
        // not nullable, there should be an existing value,
        // so nothing to do here, if we need to circle back:
        // and, put back what was there before:
        // setCurrentDate(getMoment()(currentDate));
        return;
      }
      setCurrentDate(date);
      submitDateTimeToServer(date);
    },
    [editingContextId, isNullable, path, submitDateTimeToServer]
  );

  const onClear = useCallback(() => {
    if (isNullable) {
      //Previously we were passing onClear in case of nullable only but if we want to do some sutff if the date is not nullable we can do that.
      onChange();
    }
  }, [isNullable, onChange]);

  return (
    <EditorLabel editor={child} path={path} doNotWrap hideInputLabel={hideInputLabel}>
      <DateTimePicker
        currentDate={currentDate}
        onChange={onChange}
        isDateTimePicker={isDateTimePicker || isDate === false}
        onClear={onClear}
        validation={validation}
        nullable={isNullable}
      />
    </EditorLabel>
  );
}
